import style from "../../style/ExtendReport.module.scss";
import className from "classnames/bind";
import {Fragment, useState, useEffect, memo, useCallback} from "react";
import {Table, DatePicker, Modal, Tooltip} from "antd";
import _ from "lodash";
import dayjs from "dayjs";
import ActionBar from "~/layout/ActionBar";
import {Loading} from "~/components";
import FontAwesomeIcon from "~/components/FontAwesome";
import {apiError, handleRequest, numberFormat, strToTime} from "~/utils";
import {reportApi} from "~/api";
import {Pie} from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	ArcElement,
	BarElement,
	Title,
	Tooltip as ChartTooltip,
	Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, ArcElement, BarElement, Title, ChartTooltip, Legend);

const cn = className.bind(style);

//Hiển thị số tiền: 0 -> '-'
const money = (value) => (value ? numberFormat(value) : '-');

//Cấu hình dịch vụ: nhãn + màu hiển thị
const SERVICES = [
	{key: 'host', label: 'Host', color: '#0284c7'},
	{key: 'ssl', label: 'SSL', color: '#059669'},
	{key: 'email', label: 'Email Server', color: '#6366f1'},
	{key: 'other', label: 'Dịch vụ khác', color: '#f59e0b'},
];

const emptyState = {
	summary: {revenue: 0, count: 0, sale: {revenue: 0, count: 0}, other: {revenue: 0, count: 0}},
	byPerson: [],
	total: {revenue: 0, host: 0, ssl: 0, email: 0},
	count: {revenue: 0, host: 0, ssl: 0, email: 0},
	totalSamePeriod: {revenue: 0, host: 0, ssl: 0, email: 0},
	countSamePeriod: {revenue: 0, host: 0, ssl: 0, email: 0},
	calls: {total: 0, totalSamePeriod: 0, byPerson: []},
	renewal: {new: {due: 0, renewed: 0, rate: 0}, old: {due: 0, renewed: 0, rate: 0}, byService: {}, breakdown: [], byParity: []},
	reasons: {},
};

//Tính % thay đổi so với kỳ trước -> {text, up} hoặc null
const growth = (current, previous) => {
	current = current || 0;
	previous = previous || 0;
	if (!previous) return current ? {text: 'Mới', up: true} : null;
	const pct = ((current - previous) / previous) * 100;
	if (Math.abs(pct) < 0.05) return {text: '0%', up: true};
	return {text: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`, up: pct >= 0};
};

function ExtendReport() {

	const [loading, setLoading] = useState(false);

	const [time, setTime] = useState(dayjs().startOf('month'));

	const [data, setData] = useState(emptyState);

	const [detail, setDetail] = useState({open: false, loading: false, title: '', rows: [], total: 0, kind: ''});

	const handleLoad = useCallback(async (t) => {
		setLoading(true);
		let [error, response] = await handleRequest(reportApi.extend({time: strToTime(t)}));
		let message = apiError(`Lấy thông tin báo cáo thất bại`, error, response);
		if (!message) {
			setData({...emptyState, ...(response.data || {})});
		}
		setLoading(false);
	}, []);

	useEffect(() => { handleLoad(time).then(); }, [time, handleLoad]);

	//Mở modal chi tiết theo người / theo dịch vụ / theo tỷ lệ gia hạn lại
	const openDetail = async (by, key, title, service, company) => {
		setDetail({open: true, loading: true, title, rows: [], total: 0, kind: ''});
		const [error, response] = await handleRequest(reportApi.extendDetail({by, key, service, company, time: strToTime(time)}));
		if (error) {
			setDetail((d) => ({...d, loading: false}));
			return;
		}
		setDetail((d) => ({
			...d,
			loading: false,
			kind: response.data?.kind || '',
			rows: (response.data?.rows || []).map((r, i) => ({...r, _k: i})),
			total: response.data?.total || 0,
		}));
	};

	const {summary, byPerson} = data;

	/* ----- Thẻ tổng kết ----- */
	const cards = [
		{icon: 'fa-light fa-sack-dollar', label: 'Tổng doanh số gia hạn', value: numberFormat(summary.revenue), unit: 'VND', gradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 60%, #38bdf8 100%)', delta: growth(summary.revenue, data.totalSamePeriod?.revenue), deltaAmount: data.totalSamePeriod?.revenue},
		{icon: 'fa-light fa-file-signature', label: 'Số hợp đồng gia hạn', value: numberFormat(summary.count), unit: 'hợp đồng', gradient: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 60%, #c4b5fd 100%)', delta: growth(summary.count, data.countSamePeriod?.revenue), deltaAmount: data.countSamePeriod?.revenue},
		{icon: 'fa-light fa-users', label: 'Doanh số phòng Kinh doanh', value: numberFormat(summary.sale.revenue), unit: 'VND', gradient: 'linear-gradient(135deg, #047857 0%, #059669 60%, #34d399 100%)'},
		{icon: 'fa-light fa-headset', label: 'Doanh số CSKH / khác', value: numberFormat(summary.other.revenue), unit: 'VND', gradient: 'linear-gradient(135deg, #b45309 0%, #e47d0a 60%, #f59e0b 100%)'},
	];

	/* ----- Bảng doanh số theo người ----- */
	const personColumns = [
		{
			title: 'Nhân viên',
			dataIndex: 'name',
			fixed: 'left',
			width: 170,
			render: (value, row) => (
				<div className={cn('channel')}>
					<span className={cn('dot')} style={{background: row.key === 'sale' ? '#0284c7' : '#94a3b8'}}/>
					{value}
				</div>
			),
		},
		...SERVICES.map((s) => ({
			title: s.label,
			dataIndex: ['services', s.key],
			width: 120,
			align: 'right',
			render: (value, row) => {
				if (!value) return money(value);
				return (
					<span className={cn('cellLink')} onClick={() => openDetail('person', row.key, `${s.label} — ${row.name}`, s.key)}>
						{numberFormat(value)}
					</span>
				);
			},
		})),
		{
			title: 'Doanh số',
			dataIndex: 'revenue',
			width: 140,
			align: 'right',
			render: (value, row) => {
				if (!value) return money(value);
				return (
					<span className={cn('cellLink')} onClick={() => openDetail('person', row.key, `Chi tiết gia hạn — ${row.name}`)}>
						<b>{numberFormat(value)}</b>
					</span>
				);
			},
		},
		{title: 'Số HĐ', dataIndex: 'count', width: 80, align: 'right', render: (v) => numberFormat(v)},
	];

	const personSummary = () => {
		const sv = (key) => byPerson.reduce((acc, r) => acc + (r.services?.[key] || 0), 0);
		return (
			<Table.Summary fixed>
				<Table.Summary.Row>
					<Table.Summary.Cell index={0}><b>TỔNG</b></Table.Summary.Cell>
					{SERVICES.map((s, i) => (
						<Table.Summary.Cell key={s.key} index={i + 1} align="right">{money(sv(s.key))}</Table.Summary.Cell>
					))}
					<Table.Summary.Cell index={SERVICES.length + 1} align="right"><b>{money(summary.revenue)}</b></Table.Summary.Cell>
					<Table.Summary.Cell index={SERVICES.length + 2} align="right">{numberFormat(summary.count)}</Table.Summary.Cell>
				</Table.Summary.Row>
			</Table.Summary>
		);
	};

	/* ----- Biểu đồ lý do không gia hạn ----- */
	const reasonItems = data.reasons || {};
	const reasonKeys = Object.keys(reasonItems);
	const reasonChart = {
		labels: reasonKeys.map((k) => reasonItems[k]?.label),
		datasets: [{
			data: reasonKeys.map((k) => reasonItems[k]?.count || 0),
			backgroundColor: reasonKeys.map((k) => reasonItems[k]?.color),
			borderWidth: 0,
		}],
	};

	//Hàng so sánh dịch vụ (kỳ này / cùng kỳ năm trước)
	const compareRows = [
		{key: 'host', label: 'Host', color: '#0284c7'},
		{key: 'ssl', label: 'SSL', color: '#059669'},
		{key: 'email', label: 'Email Server', color: '#6366f1'},
	];

	//Ô tỷ lệ gia hạn lại trong bảng phân rã (công ty/dịch vụ) — bấm để xem chi tiết
	const renewalCell = (row, group) => {
		const r = row[group] || {due: 0, renewed: 0, rate: 0};
		if (!r.due) return <span style={{color: '#cbd5e1'}}>—</span>;
		const groupLabel = group === 'new' ? 'Khách mới' : 'Khách cũ';
		return (
			<span
				className={cn('cellLink')}
				onClick={() => openDetail('renewal', group, `${groupLabel} gia hạn lại — ${row.label} — ${time.format('[Tháng] M/YYYY')}`, row.service, row.company)}
			>
				<b>{r.rate}%</b> <span style={{color: '#94a3b8', fontSize: 12}}>({numberFormat(r.renewed)}/{numberFormat(r.due)})</span>
			</span>
		);
	};

	return (
		<Fragment>
			<ActionBar title={'Báo cáo gia hạn'}></ActionBar>
			<div className="container d-block">
				<div className={cn('page')}>

					<div className={cn('header')}>
						<DatePicker
							picker="month"
							allowClear={false}
							format="[Tháng] M / YYYY"
							value={time}
							onChange={(d) => d && setTime(d.startOf('month'))}
							style={{width: 170}}
						/>
					</div>

					{/* Thẻ tổng kết */}
					<div className={cn('cards')}>
						{cards.map((c, i) => (
							<div key={i} className={cn('card')}>
								<div className={cn('icon')} style={{background: c.gradient}}>
									<FontAwesomeIcon icon={c.icon}/>
								</div>
								<div style={{minWidth: 0}}>
									<p className={cn('label')}>{c.label}</p>
									<p className={cn('value')}>{c.value}</p>
									<p className={cn('unit')}>
										{c.unit}
										{c.delta && <span className={cn('delta', c.delta.up ? 'up' : 'down')} title={`Cùng kỳ năm trước: ${numberFormat(c.deltaAmount || 0)}`}>
											<FontAwesomeIcon icon={c.delta.up ? 'fa-solid fa-arrow-trend-up' : 'fa-solid fa-arrow-trend-down'}/> {c.delta.text}
											<span className={cn('deltaNote')}> so năm trước</span>
										</span>}
									</p>
								</div>
							</div>
						))}
					</div>

					{/* Doanh số gia hạn theo người */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Doanh số gia hạn theo người</div>
						<div className={cn('panelBody')}>
							{loading && <Loading/>}
							{!loading &&
								<div className={cn('table')}>
									<Table
										rowKey="key"
										size="small"
										bordered
										columns={personColumns}
										dataSource={byPerson}
										pagination={false}
										scroll={{x: 'max-content'}}
										summary={personSummary}
										locale={{emptyText: 'Không có hợp đồng gia hạn trong kỳ'}}
									/>
								</div>
							}
						</div>
					</div>

					{/* Số cuộc gọi gia hạn */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Số cuộc gọi gia hạn</div>
						<div className={cn('panelBody')}>
							<div className={cn('grid2')}>
								<div className={cn('callTotal')}>
									<div className={cn('callTotalIcon')}>
										<FontAwesomeIcon icon={'fa-light fa-phone-volume'}/>
									</div>
									<div>
										<p className={cn('callTotalValue')}>{numberFormat(data.calls?.total || 0)}</p>
										<p className={cn('callTotalLabel')}>cuộc gọi gia hạn trong tháng</p>
										{(() => {
											const d = growth(data.calls?.total, data.calls?.totalSamePeriod);
											return d ? (
												<span className={cn('delta', d.up ? 'up' : 'down')}>
													<FontAwesomeIcon icon={d.up ? 'fa-solid fa-arrow-trend-up' : 'fa-solid fa-arrow-trend-down'}/> {d.text}
													<span className={cn('deltaNote')}> so năm trước ({numberFormat(data.calls?.totalSamePeriod || 0)})</span>
												</span>
											) : null;
										})()}
									</div>
								</div>
								<div className={cn('table')}>
									<Table
										rowKey="key"
										size="small"
										bordered
										pagination={false}
										dataSource={data.calls?.byPerson || []}
										locale={{emptyText: 'Không có cuộc gọi gia hạn trong kỳ'}}
										columns={[
											{
												title: 'Nhân viên',
												dataIndex: 'name',
												render: (value, row) => (
													<div className={cn('channel')}>
														<span className={cn('dot')} style={{background: row.key === 'sale' ? '#0284c7' : '#94a3b8'}}/>
														{value}
													</div>
												),
											},
											{title: 'Số cuộc gọi', dataIndex: 'count', width: 130, align: 'right', render: (v) => <b>{numberFormat(v)}</b>},
										]}
										summary={() => (
											<Table.Summary fixed>
												<Table.Summary.Row>
													<Table.Summary.Cell index={0}><b>TỔNG</b></Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right"><b>{numberFormat(data.calls?.total || 0)}</b></Table.Summary.Cell>
												</Table.Summary.Row>
											</Table.Summary>
										)}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Tỷ lệ gia hạn lại theo tháng hết hạn */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>
							Tỷ lệ gia hạn lại theo tháng hết hạn
							<Tooltip title="Tính theo dịch vụ có kỳ kết thúc trong tháng này. Dịch vụ hết hạn tháng này dù gia hạn ở tháng sau vẫn được tính vào tháng này.">
								<span style={{marginLeft: 6, color: '#94a3b8', cursor: 'help'}}>
									<FontAwesomeIcon icon={'fa-light fa-circle-info'}/>
								</span>
							</Tooltip>
						</div>
						<div className={cn('panelBody')}>
							<div className={cn('grid4')}>
								{[
									{group: 'new', service: null, label: 'Khách mới gia hạn lại', desc: 'Dịch vụ lần đầu đến hạn (HĐ mới)', color: '#0284c7', icon: 'fa-light fa-rotate'},
									{group: 'old', service: null, label: 'Khách cũ gia hạn lại', desc: 'Dịch vụ đã từng gia hạn trước đó', color: '#059669', icon: 'fa-light fa-rotate'},
									{group: 'new', service: 'host', label: 'Khách mới gia hạn lại (Host)', desc: 'Dịch vụ Host lần đầu đến hạn (HĐ mới)', color: '#0ea5e9', icon: 'fa-light fa-server'},
									{group: 'old', service: 'host', label: 'Khách cũ gia hạn lại (Host)', desc: 'Dịch vụ Host đã từng gia hạn trước đó', color: '#0d9488', icon: 'fa-light fa-server'},
								].map((g) => {
									const r = (g.service ? data.renewal?.byService?.[g.service]?.[g.group] : data.renewal?.[g.group]) || {due: 0, renewed: 0, rate: 0};
									return (
										<div key={`${g.service || 'all'}-${g.group}`} className={cn('callTotal')}>
											<div className={cn('callTotalIcon')} style={{background: g.color}}>
												<FontAwesomeIcon icon={g.icon}/>
											</div>
											<div>
												<p className={cn('callTotalValue')} style={{color: g.color}}>{r.rate}%</p>
												<p className={cn('callTotalLabel')}>{g.label}</p>
												<p className={cn('callTotalLabel')} style={{marginTop: 2}}>
													{numberFormat(r.renewed)} / {numberFormat(r.due)} dịch vụ đã gia hạn lại
												</p>
												<p className={cn('callTotalLabel')} style={{marginTop: 2, color: '#94a3b8', fontSize: 12}}>{g.desc}</p>
												{r.due > 0 &&
													<span
														className={cn('cellLink')}
														style={{display: 'inline-block', marginTop: 6, fontSize: 13}}
														onClick={() => openDetail('renewal', g.group, `${g.label} — ${time.format('[Tháng] M/YYYY')}`, g.service || undefined)}
													>
														<FontAwesomeIcon icon={'fa-light fa-list'}/> Xem chi tiết
													</span>
												}
											</div>
										</div>
									);
								})}
							</div>

							{/* Phân rã theo công ty (Sikido / D&H) -> dịch vụ (host/ssl/email) */}
							{(data.renewal?.breakdown || []).length > 0 &&
								<div className={cn('table')} style={{marginTop: 16}}>
									<Table
										key={`renewal-bd-${time.format('YYYY-MM')}`}
										rowKey="key"
										size="small"
										bordered
										pagination={false}
										scroll={{x: 'max-content'}}
										defaultExpandAllRows
										dataSource={data.renewal.breakdown}
										locale={{emptyText: 'Không có dữ liệu'}}
										columns={[
											{
												title: 'Công ty / Dịch vụ',
												dataIndex: 'label',
												fixed: 'left',
												width: 200,
												render: (value, row) => row.service ? value : <b>{value}</b>,
											},
											{
												title: 'Khách mới gia hạn lại',
												align: 'center',
												render: (v, row) => renewalCell(row, 'new'),
											},
											{
												title: 'Khách cũ gia hạn lại',
												align: 'center',
												render: (v, row) => renewalCell(row, 'old'),
											},
										]}
									/>
								</div>
							}
						</div>
					</div>

					{/* Doanh thu gia hạn theo mã số khách (chẵn / lẻ) */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>
							Doanh thu gia hạn theo mã số khách (chẵn / lẻ)
							<Tooltip title="Neo theo dịch vụ có kỳ kết thúc trong tháng. Dự kiến = doanh thu nếu tất cả dịch vụ đến hạn đều gia hạn (100%). Thực tế = doanh thu đã thu từ các dịch vụ đã gia hạn lại.">
								<span style={{marginLeft: 6, color: '#94a3b8', cursor: 'help'}}>
									<FontAwesomeIcon icon={'fa-light fa-circle-info'}/>
								</span>
							</Tooltip>
						</div>
						<div className={cn('panelBody')}>
							<div className={cn('table')}>
								<Table
									key={`renewal-parity-${time.format('YYYY-MM')}`}
									rowKey="key"
									size="small"
									bordered
									pagination={false}
									scroll={{x: 'max-content'}}
									defaultExpandAllRows
									dataSource={data.renewal?.byParity || []}
									locale={{emptyText: 'Không có dữ liệu'}}
									columns={[
										{
											title: 'Mã số khách / Dịch vụ',
											dataIndex: 'label',
											fixed: 'left',
											width: 200,
											render: (value, row) => row.service ? value : <b>{value}</b>,
										},
										{
											title: 'DT nếu gia hạn 100%',
											align: 'right',
											width: 180,
											render: (v, row) => money(row.potential),
										},
										{
											title: 'DT thực tế thu được',
											align: 'right',
											width: 180,
											render: (v, row) => <b>{money(row.actual)}</b>,
										},
										{
											title: 'Tỷ lệ đạt',
											align: 'center',
											width: 110,
											render: (v, row) => {
												if (!row.potential) return <span style={{color: '#cbd5e1'}}>—</span>;
												return <span className={cn('trend', row.rate >= 100 ? 'up' : 'down')}>{row.rate}%</span>;
											},
										},
									]}
								/>
							</div>
						</div>
					</div>

					{/* Số tiền & số HĐ theo dịch vụ (so sánh năm trước) */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Số tiền & số HĐ theo dịch vụ</div>
						<div className={cn('panelBody')}>
							<div className={cn('table')}>
								<Table
									rowKey="key"
									size="small"
									bordered
									pagination={false}
									scroll={{x: 'max-content'}}
									dataSource={[
										...compareRows,
										{key: 'revenue', label: 'Tổng', color: '#0f172a', _total: true},
									]}
									columns={[
										{
											key: 'service',
											title: 'Dịch vụ',
											dataIndex: 'label',
											fixed: 'left',
											width: 130,
											render: (value, row) => (
												<div className={cn('channel')}>
													<span className={cn('dot')} style={{background: row.color}}/>
													{row._total ? <b>{value}</b> : value}
												</div>
											),
										},
										{key: 'revNow', title: 'Số tiền', align: 'right', width: 150, render: (v, row) => money(data.total?.[row.key])},
										{key: 'cntNow', title: 'Số HĐ', align: 'right', width: 90, render: (v, row) => numberFormat(data.count?.[row.key] || 0)},
										{
											key: 'cmpYear',
											title: 'So với năm trước',
											align: 'center',
											width: 160,
											render: (v, row) => {
												const cur = data.total?.[row.key] || 0;
												const prev = data.totalSamePeriod?.[row.key] || 0;
												const g = growth(cur, prev);
												if (!g) return <span style={{color: '#cbd5e1'}}>—</span>;
												return (
													<Tooltip title={`Cùng kỳ năm trước: ${money(prev)} · ${numberFormat(data.countSamePeriod?.[row.key] || 0)} HĐ`}>
														<span className={cn('trend', g.up ? 'up' : 'down')}>
															<FontAwesomeIcon icon={g.up ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'}/> {g.text}
														</span>
													</Tooltip>
												);
											},
										},
									]}
								/>
							</div>
						</div>
					</div>

					{/* Lý do không gia hạn */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Lý do không gia hạn</div>
						<div className={cn('panelBody')}>
							{_.isEmpty(reasonItems) && <p style={{color: '#94a3b8', margin: 0}}>Không có dữ liệu</p>}
							{!_.isEmpty(reasonItems) &&
								<div className={cn('grid2')}>
									<div className={cn('chartBox')}>
										{!loading && <Pie options={{plugins: {legend: {position: 'bottom'}}}} data={reasonChart}/>}
									</div>
									<div className={cn('statList')}>
										{reasonKeys.map((k) => (
											<div key={k} className={cn('statRow')}>
												<span className={cn('dot')} style={{background: reasonItems[k]?.color}}/>
												<span className={cn('statName')}>{reasonItems[k]?.label}</span>
												<span className={cn('statValue')}>{numberFormat(reasonItems[k]?.count || 0)}</span>
											</div>
										))}
									</div>
								</div>
							}
						</div>
					</div>

				</div>
			</div>

			<Modal
				open={detail.open}
				title={detail.title}
				footer={null}
				width={960}
				onCancel={() => setDetail((d) => ({...d, open: false}))}
			>
				{detail.loading ? <Loading/> :
					(detail.kind === 'renewal' ?
						<Table
							rowKey="_k"
							size="small"
							pagination={false}
							scroll={{y: 420}}
							locale={{emptyText: 'Không có dữ liệu'}}
							columns={[
								{title: 'Khách hàng / HĐ', dataIndex: 'label'},
								{title: 'Tên miền', dataIndex: 'domain', width: 180, render: (v) => v || '-'},
								{title: 'Dịch vụ', dataIndex: 'service', width: 110},
								{title: 'Ngày hết hạn', dataIndex: 'expired', width: 120, render: (t) => t ? dayjs.unix(t).format('DD/MM/YYYY') : '-'},
								{
									title: 'Trạng thái',
									dataIndex: 'renewed',
									width: 170,
									render: (renewed, row) => renewed
										? <span style={{color: '#059669'}}><FontAwesomeIcon icon={'fa-solid fa-circle-check'}/> Đã gia hạn{row.renewedDate ? ` ${dayjs.unix(row.renewedDate).format('DD/MM/YYYY')}` : ''}</span>
										: <span style={{color: '#dc2626'}}><FontAwesomeIcon icon={'fa-solid fa-circle-xmark'}/> Chưa gia hạn</span>,
								},
							]}
							dataSource={detail.rows}
						/>
						:
						<Table
							rowKey="_k"
							size="small"
							pagination={false}
							scroll={{y: 420}}
							locale={{emptyText: 'Không có dữ liệu'}}
							columns={[
								{title: 'Khách hàng / HĐ', dataIndex: 'label'},
								{title: 'Dịch vụ', dataIndex: 'service', width: 120},
								{title: 'Ngày gia hạn', dataIndex: 'date', width: 130, render: (t) => t ? dayjs.unix(t).format('DD/MM/YYYY') : '-'},
								{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
							]}
							dataSource={detail.rows}
							summary={() => (
								<Table.Summary fixed>
									<Table.Summary.Row>
										<Table.Summary.Cell index={0} colSpan={3}><b>Tổng</b></Table.Summary.Cell>
										<Table.Summary.Cell index={1} align="right"><b>{money(detail.total)}</b></Table.Summary.Cell>
									</Table.Summary.Row>
								</Table.Summary>
							)}
						/>
					)
				}
			</Modal>
		</Fragment>
	)
}

export default memo(ExtendReport);
